// The gesture under way (underWay.ts), as the UI asks it: one row per kind — the
// button-held drag (main.ts `DRAG_UNDER_WAY`, over DRAG_LIFE), a handle's work
// (handleWork.ts, the move gizmo's too) and a grip's (grips.ts) — and one call
// for each thing every place that ends "whatever is under way" needs. No caller
// ends a kind by name (test/underWay.test.mjs reads them). docs/claude/tools.md,
// *The gesture under way*.
import {
  type UnderWay,
  type UnderWayKind,
  underWayLife,
  type UnderWayPointer,
  type UnderWayRow,
} from '../underWay';
import { DRAG_UNDER_WAY } from '../main';
import { HANDLE_UNDER_WAY } from './handleWork';
import { GRIP_UNDER_WAY } from './grips';

/** Each kind's row: a Record, so a new kind does not compile until it has one.
 *  Built at the first ask (the rows' modules import this one). */
let rows: Record<UnderWayKind, UnderWayRow> | null = null;
let life: UnderWay | null = null;
const underWay = (): UnderWay => (life ??= underWayLife(underWayRows()));
const underWayRows = (): Record<UnderWayKind, UnderWayRow> => (rows ??= { drag: DRAG_UNDER_WAY, grip: GRIP_UNDER_WAY, handle: HANDLE_UNDER_WAY });

/** A kind's row: a drag that is a work's held phase (DRAG_LIFE `work`) is put
 *  back through it. */
export const underWayRow = (k: UnderWayKind): UnderWayRow => underWayRows()[k];
/** What is under way (the first in the order), else null. */
export const underWayNow = (): UnderWayKind | null => underWay().now();
/** Put back whatever is under way: Esc, Undo / Redo, a tab switch, closing the
 *  3D view. True when something was. */
export const cancelUnderWay = (): boolean => underWay().cancel();
/** The pointer was lost: what holds it goes back (an armed work stays). */
export const cancelHeldUnderWay = (): boolean => underWay().cancelHeld();
/** End where it is whatever a key or a click can end: Enter, Space, a
 *  right-click, a pinch. True when something was. */
export const finishUnderWay = (): boolean => underWay().finish();
/** A press elsewhere on the page drops an armed work where it is (`inside`:
 *  whether the press is on the space it is worked in). */
export const finishArmedOffUnderWay = (inside: (space: 'plan' | '3d') => boolean): void => { underWay().finishArmedOff(inside); };
/** The drawing was replaced (a load): what is under way only ends. */
export const dropUnderWay = (): void => { underWay().drop(); };
/** Once a frame: what is under way ends if what owns it changed. */
export const checkUnderWay = (): void => { underWay().check(); };
/** The pointer moved: true when what is under way took it. */
export const moveUnderWayAt = (p: UnderWayPointer): boolean => underWay().move(p);
/** A press: true when what is under way took it (an armed work dropped there). */
export const pressUnderWay = (p: UnderWayPointer): boolean => underWay().press(p);
/** Whether typing goes to the bar for what is under way. */
export const takesTypedUnderWay = (): boolean => underWay().takesTyped();
/** A typed figure committed to what is under way. True when one took it. */
export const commitTypedUnderWay = (): boolean => underWay().typed();
/** The bar's prompt while something is under way (null: the tool's). */
export const underWayPrompt = (): string | null => underWay().hint();
/** Esc: a figure typed for what is under way first, else it goes back. */
export const escUnderWay = (entry: boolean): 'entry' | 'cancel' | null => underWay().escape(entry);
